'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { parseISO } from 'date-fns';
import { Evento, Iniziativa } from '@/types';
import { LABELS } from '@/lib/labels';
import { formatDataCompleta } from '@/lib/dateUtils';
import { getSpazioImage, getSpazioText } from '@/lib/spazioUtils';

interface Props {
  evento: Evento | null;
  iniziativa: Iniziativa;
}

export default function ProssimaSerataSection({ evento, iniziativa }: Props) {
  const [prossimoEvento, setProssimoEvento] = useState<Evento | null>(evento);

  // Al mount aggiorna con i dati freschi da events.json per garantire
  // che il prossimo evento rifletta la data corrente e non quella di build.
  useEffect(() => {
    const buildTime = process.env.NEXT_PUBLIC_BUILD_TIME ?? '0';
    fetch(`/events.json?v=${buildTime}`)
      .then((r) => r.json())
      .then((data: Evento[]) => {
        const oggi = new Date();
        oggi.setHours(0, 0, 0, 0);
        const prossimo = data.find((e) => parseISO(e.data) >= oggi) ?? null;
        setProssimoEvento(prossimo);
      })
      .catch(() => {});
  }, []);

  const sports = iniziativa.sportInProgramma ?? [];
  const menu = iniziativa.menuSerata ?? [];
  const spazi = iniziativa.spaziAperti ?? [];
  const dataLabel = prossimoEvento ? formatDataCompleta(prossimoEvento.data) : null;

  return (
    <section id="prossima-serata" className="bg-board-dark py-10 px-4">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Link prossima serata */}
        {dataLabel && prossimoEvento && prossimoEvento.stato !== 'non-attivo' && prossimoEvento.pubblicato && (
          <p className="text-center text-chalk-muted text-sm uppercase tracking-widest">
            <Link
              href={`/eventi/${prossimoEvento.slug}`}
              className="hover:text-accent-gold transition-colors"
            >
              Guarda i dettagli della prossima serata:{' '}
              <span className="font-bold text-accent-gold capitalize">{dataLabel}</span>
            </Link>
          </p>
        )}

        {/* Card eventi sportivi */}
        {sports.length > 0 && (
          <div className="bg-section-green rounded-2xl p-6 md:p-8">
            <h2 className="font-section text-2xl md:text-3xl text-chalk text-center uppercase tracking-wide mb-6">
              {LABELS.eventiSportivi.titoloSezione}
            </h2>
            <div className="flex flex-row items-center justify-around gap-4">
              {sports.map((sport, i) => (
                <div key={i} className="flex items-center justify-center flex-1 min-h-[80px]">
                  {sport.logo ? (
                    <div className="relative h-16 w-full max-w-[160px]">
                      <Image src={sport.logo} alt={sport.altLogo ?? sport.nome ?? ''} fill className="object-contain" />
                    </div>
                  ) : null}
                </div>
              ))}
              <div className="flex flex-col items-center justify-center flex-1 min-h-[80px]">
                <span className="text-sm font-chalk font-bold text-chalk text-center uppercase leading-tight">
                  {LABELS.eventiSportivi.altroAncora}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Menu + Attivita */}
        <div className="grid md:grid-cols-2 gap-4">

          {/* MENU DELLA SERATA */}
          <div className="bg-section-orange rounded-2xl p-6 md:p-8">
            <h2 className="font-section text-2xl md:text-3xl text-chalk uppercase tracking-wide text-center mb-6">
              {LABELS.menu.titoloSezione}
            </h2>
            {menu.length > 0 ? (
              <div className={`grid gap-4 ${menu.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                {menu.map((item, i) => (
                  <div key={i} className="flex flex-col items-center gap-2">
                    {item.immagine ? (
                      <div className="relative w-20 h-20">
                        <Image src={item.immagine} alt={item.nome} fill className="object-contain drop-shadow-md" />
                      </div>
                    ) : (
                      <div className="w-20 h-20 flex items-center justify-center bg-white/50 rounded-xl text-4xl font-chalk font-bold text-chalk-dim" aria-hidden="true">
                        {item.nome.charAt(0).toUpperCase()}
                      </div>
                    )}
                    {item.prezzo && (
                      <span className="font-bold text-chalk text-lg">{item.prezzo}</span>
                    )}
                    <span className="text-chalk-dim text-xs text-center leading-tight">{item.nome}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-chalk-dim text-sm text-center">{LABELS.menu.vuoto}</p>
            )}
          </div>

          {/* ATTIVITA & SPORT */}
          <div className="bg-section-blue rounded-2xl p-6 md:p-8">
            <h2 className="font-section text-2xl md:text-3xl text-chalk uppercase tracking-wide text-center mb-6">
              {LABELS.attivita.titoloSezione}
            </h2>
            <div className="grid grid-cols-3 gap-3">
              {spazi.map((spazio, i) => (
                <div key={i} className="flex flex-col items-center gap-2 p-3 rounded-xl">
                  <Image src={getSpazioImage(spazio)} alt={getSpazioText(spazio)} width={48} height={48} className="object-contain" aria-hidden="true" />
                  <span className="text-xs text-chalk text-center leading-snug font-medium">{getSpazioText(spazio)}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}